import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";

import { SearchField } from "@/common/components/search-field/SearchField";
import { useTranslation } from "@/translations";

import { TakeoverHistoryFilterBar } from "./components/TakeoverHistoryFilterBar";
import { TakeoverHistoryTable } from "./components/TakeoverHistoryTable";
import { useTakeoverHistory } from "./hooks/useTakeoverHistory";

interface Props {
  onClose: () => void;
}

/** "Geschichte – Übernahmen": a fixed-height dialog listing all past appointment takeovers, with a search
 *  box under the title, a date + Bearbeiter:in filter bar, and a scrollable read-only table. */
export function TakeoverHistoryPanel({ onClose }: Props) {
  const { t } = useTranslation();
  const { formik, rows, isLoading, serviceOptions, serviceFilter, setServiceFilter, setSearchText } =
    useTakeoverHistory();

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth slotProps={{ paper: { sx: { height: "85vh" } } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Stack spacing={0.25}>
          <Typography variant="h2" component="span">
            {t("takeover.history.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("takeover.history.countLabel", { count: rows.length })}
          </Typography>
        </Stack>
        <IconButton aria-label={t("common.close")} onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, overflow: "hidden", pt: 2 }}>
        <SearchField placeholder={t("takeover.history.searchPlaceholder")} onChange={setSearchText} width="100%" />
        <TakeoverHistoryFilterBar formik={formik} />
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <TakeoverHistoryTable
            rows={rows}
            isLoading={isLoading}
            serviceOptions={serviceOptions}
            serviceFilter={serviceFilter}
            onServiceFilterChange={setServiceFilter}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
